import { useMemo, useState, useRef } from 'react';
import styled from '@emotion/styled';
import { Modal, Button, Progress } from 'semantic-ui-react';
import { prestadores_evaluacion_questions } from './quiz/prestadores_evaluacion_questions';
import { crecimiento_directores_evaluacion_questions } from './quiz/crecimiento_directores_evaluacion_questions';
import { screenSizes } from 'src/assets/styles/Literals';
import { useReactToPrint } from 'react-to-print';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const StyledModalHeader = styled(Modal.Header)`
  font-size: 20px;
  font-weight: 600;
  word-wrap: break-word;

  @media (max-width: ${screenSizes.tablet}) {
    font-size: 18px;
  }

  @media (max-width: ${screenSizes.mobile}) {
    font-size: 16px;
    padding: 1rem;
  }
`;

const StyledModalContent = styled(Modal.Content)`
  padding: 1rem;

  @media (max-width: ${screenSizes.tablet}) {
    padding: 0.875rem;
  }

  @media (max-width: ${screenSizes.mobile}) {
    padding: 0.75rem;
  }

  .summary-container {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 1rem;
    border-radius: 8px;
    margin-bottom: 1rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

    @media (max-width: ${screenSizes.tablet}) {
      padding: 0.875rem;
      margin-bottom: 0.875rem;
    }

    @media (max-width: ${screenSizes.mobile}) {
      padding: 0.75rem;
      margin-bottom: 0.75rem;
      border-radius: 6px;
    }

    h3 {
      margin: 0;
      margin-bottom: 0.5rem;
      font-size: 1.25rem;

      @media (max-width: ${screenSizes.mobile}) {
        font-size: 1.1rem;
        margin-bottom: 0.5rem;
      }
    }
  }

  .summary-stats {
    display: flex;
    gap: 1.5rem;
    flex-wrap: wrap;
    margin-top: 0.5rem;

    @media (max-width: ${screenSizes.tablet}) {
      gap: 1rem;
    }

    @media (max-width: ${screenSizes.mobile}) {
      gap: 0.75rem;
      margin-top: 0.5rem;
      flex-direction: column;
    }
  }

  .results-container {
    padding: 0;
  }

  .pie-chart-container {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    width: 100%;
    min-height: 300px;

    @media (max-width: ${screenSizes.tablet}) {
      min-height: 250px;
    }

    @media (max-width: ${screenSizes.mobile}) {
      min-height: 200px;
    }
  }

  .question-result {
    margin-bottom: 1rem;
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 8px;
    border-left: 4px solid #3498db;
    overflow-wrap: break-word;
    word-wrap: break-word;

    @media (max-width: ${screenSizes.tablet}) {
      margin-bottom: 0.875rem;
      padding: 0.875rem;
    }

    @media (max-width: ${screenSizes.mobile}) {
      margin-bottom: 0.75rem;
      padding: 0.75rem;
      border-left-width: 3px;
      border-radius: 6px;
    }

    .question-title {
      color: #2c3e50;
      font-size: 18px;
      font-weight: 600;
      margin-bottom: 0.5rem;
      padding-bottom: 0.375rem;
      border-bottom: 2px solid #e9ecef;
      word-wrap: break-word;
      overflow-wrap: break-word;
      hyphens: auto;

      @media (max-width: ${screenSizes.tablet}) {
        font-size: 17px;
        margin-bottom: 0.5rem;
      }

      @media (max-width: ${screenSizes.mobile}) {
        font-size: 16px;
        margin-bottom: 0.5rem;
        padding-bottom: 0.25rem;
        line-height: 1.4;
      }
    }

    .question-type {
      color: #6c757d;
      font-size: 12px;
      font-style: italic;
      margin-bottom: 0.375rem;

      @media (max-width: ${screenSizes.mobile}) {
        font-size: 11px;
        margin-bottom: 0.25rem;
      }
    }

    .answer-stats {
      margin-top: 0.5rem;

      @media (max-width: ${screenSizes.mobile}) {
        margin-top: 0.5rem;
      }
    }

    .option-stat {
      margin-bottom: 0.75rem;
      padding: 0.5rem;
      background: white;
      border-radius: 4px;

      @media (max-width: ${screenSizes.tablet}) {
        padding: 0.5rem;
        margin-bottom: 0.625rem;
      }

      @media (max-width: ${screenSizes.mobile}) {
        padding: 0.5rem;
        margin-bottom: 0.5rem;
      }

      .option-header {
        display: flex;
        align-items: center;
        margin-bottom: 0.375rem;
        flex-wrap: wrap;
        gap: 0.5rem;

        @media (max-width: ${screenSizes.tablet}) {
          flex-direction: column;
          align-items: flex-start;
          gap: 0.5rem;
        }

        @media (max-width: ${screenSizes.mobile}) {
          flex-direction: column;
          align-items: flex-start;
          margin-bottom: 0.25rem;
          gap: 0.375rem;
        }
      }

      .option-label {
        flex: 1;
        font-weight: 500;
        color: #2c3e50;
        min-width: 0;
        word-wrap: break-word;
        overflow-wrap: break-word;

        @media (max-width: ${screenSizes.tablet}) {
          width: 100%;
          font-size: 0.95rem;
        }

        @media (max-width: ${screenSizes.mobile}) {
          width: 100%;
          font-size: 0.9rem;
        }
      }

      .option-count {
        color: #6c757d;
        font-size: 14px;
        min-width: 60px;
        text-align: right;
        flex-shrink: 0;
        white-space: nowrap;

        @media (max-width: ${screenSizes.tablet}) {
          font-size: 13px;
          min-width: auto;
          text-align: left;
          width: 100%;
        }

        @media (max-width: ${screenSizes.mobile}) {
          font-size: 12px;
          min-width: auto;
          text-align: left;
        }
      }

      .option-progress {
        width: 200px;
        flex-shrink: 0;

        @media (max-width: ${screenSizes.tablet}) {
          width: 100%;
          margin-top: 0.25rem;
        }

        @media (max-width: ${screenSizes.mobile}) {
          width: 100%;
          margin-top: 0.25rem;
        }
      }

      .option-users {
        margin-top: 0.5rem;
        padding-top: 0.5rem;
        border-top: 1px solid #e9ecef;
        font-size: 12px;
        color: #6c757d;
        word-wrap: break-word;

        @media (max-width: ${screenSizes.mobile}) {
          font-size: 11px;
          margin-top: 0.375rem;
          padding-top: 0.375rem;
        }

        strong {
          display: block;
          margin-bottom: 0.5rem;
          cursor: pointer;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
          font-weight: 600;
          color: #2c3e50;
          font-size: 0.875rem;
          transition: color 0.2s ease;

          &:hover {
            color: #3498db;
          }

          @media (max-width: ${screenSizes.mobile}) {
            font-size: 0.8rem;
            margin-bottom: 0.375rem;
          }
        }

        .user-list {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          margin-top: 0.5rem;
          align-items: center;

          @media (max-width: ${screenSizes.tablet}) {
            gap: 0.625rem;
            margin-top: 0.375rem;
          }

          @media (max-width: ${screenSizes.mobile}) {
            gap: 0.5rem;
            margin-top: 0.375rem;
          }

          > strong {
            width: 100%;
            margin-bottom: 0.5rem;
            margin-top: 0;
            font-weight: 600;
            color: #2c3e50;
            font-size: 0.875rem;

            @media (max-width: ${screenSizes.mobile}) {
              font-size: 0.8rem;
              margin-bottom: 0.375rem;
            }
          }
        }

        .user-tag {
          display: inline-flex;
          align-items: center;
          padding: 0.375rem 0.75rem;
          background: #667eea;
          color: white;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 500;
          word-wrap: break-word;
          max-width: 100%;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          transition: all 0.2s ease;
          border: none;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          margin: 0;

          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
          }

          @media (max-width: ${screenSizes.tablet}) {
            font-size: 0.7rem;
            padding: 0.3rem 0.625rem;
          }

          @media (max-width: ${screenSizes.mobile}) {
            font-size: 0.65rem;
            padding: 0.25rem 0.5rem;
            white-space: normal;
          }
        }
      }
    }

    .text-answers-list {
      margin-top: 0.5rem;

      .text-answer-item {
        padding: 0.5rem;
        margin-bottom: 0.375rem;
        background: white;
        border-radius: 4px;
        border-left: 3px solid #3498db;
        color: #495057;
        font-size: 14px;
        line-height: 1.4;
        word-wrap: break-word;
        overflow-wrap: break-word;

        @media (max-width: ${screenSizes.tablet}) {
          padding: 0.5rem;
          font-size: 13px;
        }

        @media (max-width: ${screenSizes.mobile}) {
          padding: 0.5rem;
          font-size: 13px;
          margin-bottom: 0.25rem;
        }

        .quote-text {
          font-style: italic;
          margin-bottom: 0.375rem;
          padding-left: 0.5rem;
          border-left: 2px solid #3498db;
          word-wrap: break-word;
          overflow-wrap: break-word;
          line-height: 1.3;

          @media (max-width: ${screenSizes.mobile}) {
            padding-left: 0.375rem;
            margin-bottom: 0.25rem;
            font-size: 0.9em;
            line-height: 1.25;
          }
        }

        .quote-author {
          font-size: 12px;
          color: #6c757d;
          margin-top: 0.375rem;
          font-weight: 500;

          @media (max-width: ${screenSizes.mobile}) {
            font-size: 11px;
            margin-top: 0.25rem;
          }
        }
      }
    }

    .no-answers {
      color: #6c757d;
      font-style: italic;
      padding: 1rem;
      text-align: center;
    }
  }

  .section-title {
    background: #2c3e50;
    color: white;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    margin: 1rem 0 0.5rem 0;
    font-size: 20px;
    font-weight: 600;
    word-wrap: break-word;
    overflow-wrap: break-word;

    @media (max-width: ${screenSizes.tablet}) {
      padding: 0.625rem 0.875rem;
      margin: 0.875rem 0 0.5rem 0;
      font-size: 19px;
    }

    @media (max-width: ${screenSizes.mobile}) {
      padding: 0.5rem 0.75rem;
      margin: 0.75rem 0 0.5rem 0;
      font-size: 18px;
      border-radius: 6px;
    }
  }

  .empty-state-container {
    text-align: center;
    padding: 3rem;
    color: #6c757d;
    font-size: 16px;

    @media (max-width: ${screenSizes.tablet}) {
      padding: 2rem;
      font-size: 15px;
    }

    @media (max-width: ${screenSizes.mobile}) {
      padding: 1.5rem;
      font-size: 14px;
    }
  }

  .answer-info-text {
    margin-bottom: 0.5rem;
    color: #6c757d;
    font-size: 14px;
    word-wrap: break-word;

    @media (max-width: ${screenSizes.tablet}) {
      font-size: 13px;
      margin-bottom: 0.5rem;
    }

    @media (max-width: ${screenSizes.mobile}) {
      font-size: 12px;
      margin-bottom: 0.5rem;
    }
  }

  .date-range-container {
    padding: 0.75rem;
    background: white;
    border-radius: 4px;
    word-wrap: break-word;

    @media (max-width: ${screenSizes.tablet}) {
      padding: 0.625rem;
    }

    @media (max-width: ${screenSizes.mobile}) {
      padding: 0.5rem;
      font-size: 0.9rem;
    }
  }

  .more-answers-text {
    text-align: center;
    color: #6c757d;
    font-style: italic;
    margin-top: 0.5rem;
    word-wrap: break-word;

    @media (max-width: ${screenSizes.mobile}) {
      margin-top: 0.5rem;
      font-size: 0.9rem;
    }
  }

  @media print {
    padding: 0.5rem;
    background: white;
    color: black;

    .summary-container {
      background: #667eea !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      page-break-inside: avoid;
      margin-bottom: 0.75rem;
      padding: 0.75rem;
    }

    .question-result {
      page-break-inside: avoid;
      break-inside: avoid;
      background: #f8f9fa !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      margin-bottom: 0.75rem;
      padding: 0.75rem;
    }

    .section-title {
      background: #2c3e50 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      page-break-inside: avoid;
      margin: 0.75rem 0 0.5rem 0;
      padding: 0.625rem 0.875rem;
    }

    .option-stat {
      margin-bottom: 0.5rem;
      padding: 0.5rem;
    }

    .text-answer-item {
      margin-bottom: 0.25rem;
      padding: 0.5rem;
    }

    .pie-chart-container {
      page-break-inside: avoid;
      break-inside: avoid;
      min-height: 250px;
    }

    .user-tag {
      background: #667eea !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      color: white !important;
      box-shadow: none;
      border: 1px solid #555;
    }

    .option-users-section {
      background: #f8f9fa !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

const ShowUsersContainer = styled.div`
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e9ecef;

  .toggle-button {
    cursor: pointer;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
    font-weight: 600;
    color: #3498db;
    font-size: 0.875rem;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;

    &:hover {
      background-color: #f0f7ff;
      color: #2980b9;
    }

    @media (max-width: ${screenSizes.mobile}) {
      font-size: 0.8rem;
    }
  }

  .option-users-section {
    margin-top: 0.75rem;
    margin-bottom: 0.75rem;
    padding: 0.75rem;
    background: #f8f9fa;
    border-radius: 6px;
    border-left: 3px solid #3498db;

    @media (max-width: ${screenSizes.mobile}) {
      padding: 0.5rem;
      margin-top: 0.5rem;
      margin-bottom: 0.5rem;
    }

    .option-label {
      font-size: 0.9rem;
      font-weight: 600;
      color: #2c3e50;
      margin-bottom: 0.5rem;
      display: block;

      @media (max-width: ${screenSizes.mobile}) {
        font-size: 0.85rem;
        margin-bottom: 0.375rem;
      }
    }
  }

  @media print {
    border-top: 1px solid #ddd;
    margin-top: 0.5rem;
    padding-top: 0.5rem;

    .option-users-section {
      page-break-inside: avoid;
      break-inside: avoid;
      background: #f8f9fa !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

// Helper function to get questions dynamically based on entry content
const getQuestions = (entryContent) => {
  if (entryContent === 'prestadores_evaluacion') {
    return prestadores_evaluacion_questions({});
  } else if (entryContent === 'crecimiento_directores_evaluacion') {
    return crecimiento_directores_evaluacion_questions({});
  }
  return [];
};

// Helper function to condense nested answers
const condenseAnswers = (answers) => {
  let condensed = {};
  for (let key in answers) {
    if (typeof answers[key] === 'object' && !Array.isArray(answers[key]) && answers[key] !== null) {
      condensed = { ...condensed, ...answers[key] };
    } else {
      condensed[key] = answers[key];
    }
  }
  return condensed;
};

// Aggregate answers from all interactions
const aggregateAnswers = (interactions) => {
  const aggregated = {};
  const allAnswers = [];

  interactions.forEach((interaction) => {
    if (interaction.content?.answers) {
      const condensed = condenseAnswers(interaction.content.answers);
      // Store answer with user_id for tracking
      allAnswers.push({
        answers: condensed,
        userId: interaction.user_id,
      });
    }
  });

  // Get all unique question IDs
  const allQuestionIds = new Set();
  allAnswers.forEach(({ answers }) => {
    Object.keys(answers).forEach((key) => allQuestionIds.add(key));
  });

  // Aggregate by question ID, keeping user info
  allQuestionIds.forEach((questionId) => {
    const values = allAnswers
      .map(({ answers, userId }) => {
        const value = answers[questionId];
        if (value !== undefined && value !== null && value !== '') {
          return { answer: value, userId };
        }
        return null;
      })
      .filter((val) => val !== null);

    if (values.length > 0) {
      aggregated[questionId] = values;
    }
  });

  return aggregated;
};

// Helper function to match an answer to an option
// Handles cases where answers might be stored as numbers (1, 2, 3) or strings ("1", "2")
// while options are full text like "1. Totalmente en desacuerdo"
const matchAnswerToOption = (answer, options) => {
  // First try exact match
  if (options.includes(answer)) {
    return answer;
  }

  // Try to extract number from answer
  const answerStr = String(answer).trim();
  const numberMatch = answerStr.match(/^(\d+)/);

  if (numberMatch) {
    const num = numberMatch[1];
    // Find option that starts with this number
    const matchedOption = options.find((opt) => {
      const optStr = String(opt);
      // Match "1. " or "1 " or starts with "1"
      // Options are like "1. Totalmente en desacuerdo"
      return (
        optStr.match(new RegExp(`^${num}\\.?\\s`)) ||
        optStr.startsWith(`${num}.`) ||
        optStr.startsWith(`${num} `)
      );
    });
    if (matchedOption) {
      return matchedOption;
    }
  }

  // Try partial match (in case answer is just the text part)
  const normalizedAnswer = answerStr.toLowerCase();
  const matchedOption = options.find((opt) => {
    const optStr = String(opt).toLowerCase();
    return optStr.includes(normalizedAnswer) || normalizedAnswer.includes(optStr);
  });

  return matchedOption || answer;
};

// Calculate statistics for a question
const calculateQuestionStats = (question, aggregatedAnswers) => {
  const answers = aggregatedAnswers[question.id] || [];
  const totalAnswers = answers.length;

  if (totalAnswers === 0) {
    return null;
  }

  if (question.type === 'select' || question.type === 'radio') {
    // Count occurrences of each option and track users
    const optionData = {};
    question.options?.forEach((option) => {
      optionData[option] = { count: 0, userIds: [] };
    });

    answers.forEach((answerData) => {
      const answer = answerData.answer || answerData; // Handle both formats
      const userId = answerData.userId;
      const matchedOption = matchAnswerToOption(answer, question.options || []);

      if (optionData.hasOwnProperty(matchedOption)) {
        optionData[matchedOption].count++;
        if (userId && !optionData[matchedOption].userIds.includes(userId)) {
          optionData[matchedOption].userIds.push(userId);
        }
      } else {
        // If no match found, still count it but use the original answer
        if (!optionData[matchedOption]) {
          optionData[matchedOption] = { count: 0, userIds: [] };
        }
        optionData[matchedOption].count++;
        if (userId && !optionData[matchedOption].userIds.includes(userId)) {
          optionData[matchedOption].userIds.push(userId);
        }
      }
    });

    return {
      type: 'select',
      total: totalAnswers,
      options: Object.entries(optionData).map(([option, data]) => ({
        option,
        count: data.count,
        percentage: totalAnswers > 0 ? ((data.count / totalAnswers) * 100).toFixed(1) : 0,
        userIds: data.userIds,
      })),
    };
  } else if (question.type === 'checkbox') {
    // For checkboxes, count how many times each option was selected and track users
    const optionData = {};
    question.options?.forEach((option) => {
      optionData[option] = { count: 0, userIds: [] };
    });

    answers.forEach((answerData) => {
      const answer = answerData.answer || answerData; // Handle both formats
      const userId = answerData.userId;

      if (Array.isArray(answer)) {
        answer.forEach((selectedOption) => {
          const matchedOption = matchAnswerToOption(selectedOption, question.options || []);
          if (optionData.hasOwnProperty(matchedOption)) {
            optionData[matchedOption].count++;
            if (userId && !optionData[matchedOption].userIds.includes(userId)) {
              optionData[matchedOption].userIds.push(userId);
            }
          } else {
            if (!optionData[matchedOption]) {
              optionData[matchedOption] = { count: 0, userIds: [] };
            }
            optionData[matchedOption].count++;
            if (userId && !optionData[matchedOption].userIds.includes(userId)) {
              optionData[matchedOption].userIds.push(userId);
            }
          }
        });
      } else {
        // Handle non-array answers (for radio checkboxes)
        const matchedOption = matchAnswerToOption(answer, question.options || []);
        if (optionData.hasOwnProperty(matchedOption)) {
          optionData[matchedOption].count++;
          if (userId && !optionData[matchedOption].userIds.includes(userId)) {
            optionData[matchedOption].userIds.push(userId);
          }
        } else {
          if (!optionData[matchedOption]) {
            optionData[matchedOption] = { count: 0, userIds: [] };
          }
          optionData[matchedOption].count++;
          if (userId && !optionData[matchedOption].userIds.includes(userId)) {
            optionData[matchedOption].userIds.push(userId);
          }
        }
      }
    });

    return {
      type: 'checkbox',
      total: totalAnswers,
      options: Object.entries(optionData).map(([option, data]) => ({
        option,
        count: data.count,
        percentage: totalAnswers > 0 ? ((data.count / totalAnswers) * 100).toFixed(1) : 0,
        userIds: data.userIds,
      })),
    };
  } else if (
    question.type === 'input' ||
    question.type === 'text' ||
    question.type === 'textArea'
  ) {
    // For text inputs, show all answers with user info
    // answers is now an array of { answer, userId }
    const answerList = answers
      .map(({ answer, userId }) => ({
        text: String(answer).trim(),
        userId,
      }))
      .filter((a) => a.text);

    // Get unique answers (by text) but keep all instances with user info
    const uniqueTexts = [...new Set(answerList.map((a) => a.text))];

    return {
      type: 'text',
      total: totalAnswers,
      uniqueCount: uniqueTexts.length,
      answers: answerList, // Keep all answers with user info
    };
  } else if (question.type === 'date') {
    // For dates, show date range
    const validDates = answers
      .map((answerData) => {
        const dateValue = answerData.answer || answerData;
        return new Date(dateValue);
      })
      .filter((d) => !isNaN(d.getTime()))
      .sort((a, b) => a - b);

    return {
      type: 'date',
      total: totalAnswers,
      minDate: validDates.length > 0 ? validDates[0] : null,
      maxDate: validDates.length > 0 ? validDates[validDates.length - 1] : null,
    };
  }

  return {
    type: 'other',
    total: totalAnswers,
    answers: answers.map((a) => (a.answer !== undefined ? a.answer : a)),
  };
};

const UsersTags = ({ users }) => {
  return (
    <div className="option-users">
      <div className="user-list">
        {users.map((userName, userIdx) => {
          return (
            <span
              key={userIdx}
              className="user-tag"
              style={{
                background: '#202749',
                color: 'white',
                padding: '0.2rem',
                borderRadius: '0.2rem',
                margin: '0.2rem',
                display: 'inline-block',
              }}
            >
              {userName}
            </span>
          );
        })}
      </div>
    </div>
  );
};

const ShowUsersTags = ({ stats, users }) => {
  const [showUsers, setShowUsers] = useState(false);
  return (
    <ShowUsersContainer>
      <div className="toggle-button" onClick={() => setShowUsers(!showUsers)}>
        <span>{showUsers ? '▼' : '▶'}</span>
        <span>{showUsers ? 'Ocultar' : 'Mostrar'} quienes respondieron</span>
      </div>
      {showUsers ? (
        <div>
          {stats.options.map((optionStat, idx) => {
            const userNames = optionStat.userIds
              ? optionStat.userIds
                  .map((userId) => {
                    const user = users.find((u) => u.id === userId);
                    return user ? user.name || user.email || 'Usuario desconocido' : null;
                  })
                  .filter((name) => name)
              : [];

            return userNames.length > 0 ? (
              <div key={idx} className="option-users-section">
                <span className="option-label">{optionStat.option}:</span>
                <UsersTags users={userNames} />
              </div>
            ) : null;
          })}
        </div>
      ) : null}
    </ShowUsersContainer>
  );
};

const QuizConsolidatedResultsModal = ({ open, onClose, entry, interactions = [], users = [] }) => {
  const contentRef = useRef(null);

  // Use react-to-print for PDF generation
  const handlePrint = useReactToPrint({
    content: () => contentRef.current,
    documentTitle: `Resultados_Encuesta_${entry?.title || 'Sin_titulo'}_${
      new Date().toISOString().split('T')[0]
    }.pdf`,
    pageStyle: `
      @page {
        size: A4;
        margin: 10mm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        * {
          page-break-inside: avoid;
        }
      }
    `,
  });

  // Get all quiz interactions for this entry
  const quizInteractions = useMemo(() => {
    return interactions.filter(
      (interaction) =>
        interaction.content?.entryId === entry?.id &&
        interaction.type === 'quiz' &&
        interaction.content?.answers
    );
  }, [interactions, entry?.id]);

  // Get questions dynamically
  const questions = useMemo(() => {
    if (!entry?.content) return [];
    return getQuestions(entry.content);
  }, [entry?.content]);

  // Aggregate all answers
  const aggregatedAnswers = useMemo(() => {
    return aggregateAnswers(quizInteractions);
  }, [quizInteractions]);

  // Filter questions to only show those with answers and that have an ID
  const questionsWithAnswers = useMemo(() => {
    return questions.filter(
      (q) => q.id && aggregatedAnswers[q.id] && aggregatedAnswers[q.id].length > 0
    );
  }, [questions, aggregatedAnswers]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="large"
      style={{
        maxWidth: '90vw',
      }}
    >
      <StyledModalHeader>
        Resultados Consolidados de la Encuesta: {entry?.title || 'Sin título'}
      </StyledModalHeader>
      <div className="results-container" ref={contentRef}>
        <StyledModalContent scrolling>
          {questionsWithAnswers.length > 0 ? (
            questions.map((question, index) => {
              if (question.textOnlyInQuestions) {
                return null;
              }
              if (question.type === 'title') {
                return (
                  <div key={`title-${index}`} className="section-title">
                    {question.text}
                  </div>
                );
              }

              // Skip questions without IDs or without answers
              if (
                !question.id ||
                !aggregatedAnswers[question.id] ||
                aggregatedAnswers[question.id].length === 0
              ) {
                return null;
              }

              const stats = calculateQuestionStats(question, aggregatedAnswers, users);
              if (!stats) return null;

              return (
                <div key={question.id} className="question-result">
                  <div className="question-title">{question.text}</div>
                  <div className="question-type">Tipo: {question.type}</div>

                  {stats.type === 'select' ? (
                    <div className="answer-stats">
                      <div className="answer-info-text">Total de respuestas: {stats.total}</div>
                      <div className="pie-chart-container">
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={stats.options.map((opt) => ({
                                name: opt.option,
                                value: opt.count,
                                percentage: opt.percentage,
                                userIds: opt.userIds || [],
                              }))}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ name, percentage, value }) => {
                                // Only show label if the value is greater than 0
                                if (value > 0 && parseFloat(percentage) > 0) {
                                  return `${name}: ${percentage}%`;
                                }
                                return null;
                              }}
                              outerRadius={100}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {stats.options.map((entry, index) => {
                                const COLORS = [
                                  '#3498db',
                                  '#e74c3c',
                                  '#2ecc71',
                                  '#f39c12',
                                  '#9b59b6',
                                  '#1abc9c',
                                  '#e67e22',
                                  '#34495e',
                                  '#16a085',
                                  '#c0392b',
                                ];
                                return (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={COLORS[index % COLORS.length]}
                                  />
                                );
                              })}
                            </Pie>
                            <Tooltip
                              formatter={(value, name, props) => [
                                `${value} (${props.payload.percentage}%)`,
                                name,
                              ]}
                            />
                            <Legend
                              formatter={(value, entry) => {
                                const option = stats.options.find((opt) => opt.option === value);
                                return `${value} (${option?.percentage}%)`;
                              }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <ShowUsersTags stats={stats} users={users} />
                    </div>
                  ) : stats.type === 'checkbox' ? (
                    <div className="answer-stats">
                      <div className="answer-info-text">Total de respuestas: {stats.total}</div>
                      {stats.options.map((optionStat, idx) => {
                        const userNames = optionStat.userIds
                          ? optionStat.userIds
                              .map((userId) => {
                                const user = users.find((u) => u.id === userId);
                                return user
                                  ? user.name || user.email || 'Usuario desconocido'
                                  : null;
                              })
                              .filter((name) => name)
                          : [];

                        return (
                          <div key={idx} className="option-stat">
                            <div className="option-header">
                              <div className="option-label">{optionStat.option}</div>
                              <div className="option-count">
                                {optionStat.count} ({optionStat.percentage}%)
                              </div>
                              <div className="option-progress">
                                <Progress
                                  percent={parseFloat(optionStat.percentage)}
                                  color="blue"
                                  size="small"
                                />
                              </div>
                            </div>
                            {userNames.length > 0 ? <UsersTags users={userNames} /> : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : stats.type === 'text' ? (
                    <div className="answer-stats">
                      <div className="answer-info-text">
                        Total de respuestas: {stats.total} | Respuestas únicas: {stats.uniqueCount}
                      </div>
                      <div className="text-answers-list">
                        {stats.answers.slice(0, 20).map((answerData, idx) => {
                          const answerText = answerData.text || answerData;
                          const userId = answerData.userId;
                          const userName = userId
                            ? users.find((u) => u.id === userId)?.name ||
                              users.find((u) => u.id === userId)?.email ||
                              'Usuario desconocido'
                            : null;

                          return (
                            <div key={idx} className="text-answer-item">
                              <div className="quote-text">"{answerText}"</div>
                              {userName && <div className="quote-author">— {userName}</div>}
                            </div>
                          );
                        })}
                        {stats.answers.length > 20 && (
                          <div className="more-answers-text">
                            ... y {stats.answers.length - 20} respuestas más
                          </div>
                        )}
                      </div>
                    </div>
                  ) : stats.type === 'date' ? (
                    <div className="answer-stats">
                      <div className="answer-info-text">Total de respuestas: {stats.total}</div>
                      {stats.minDate && stats.maxDate && (
                        <div className="date-range-container">
                          <strong>Rango de fechas:</strong>
                          <br />
                          Desde: {stats.minDate.toLocaleDateString('es-ES')}
                          <br />
                          Hasta: {stats.maxDate.toLocaleDateString('es-ES')}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="answer-stats">
                      <div className="answer-info-text">Total de respuestas: {stats.total}</div>
                      <div className="text-answers-list">
                        {stats.answers.slice(0, 10).map((answer, idx) => (
                          <div key={idx} className="text-answer-item">
                            {String(answer)}
                          </div>
                        ))}
                        {stats.answers.length > 10 && (
                          <div className="more-answers-text">
                            ... y {stats.answers.length - 10} respuestas más
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="empty-state-container">
              <p>No hay respuestas disponibles para esta encuesta aún.</p>
            </div>
          )}
        </StyledModalContent>
      </div>
      <Modal.Actions>
        <Button onClick={onClose}>Cerrar</Button>
        <Button onClick={handlePrint} primary icon="print" content="Descargar PDF" />
      </Modal.Actions>
    </Modal>
  );
};

export default QuizConsolidatedResultsModal;
